/** Shared persisted question-plan contract. Keep this module browser-safe. */
export type QuizQuestionSlotItemType = 'trivia' | 'poll';
export const RSS_MATCH_FIELDS = [
  'titleInclude',
  'titleExclude',
  'urlInclude',
  'urlExclude',
  'categoryInclude',
  'categoryExclude',
  'sectionInclude',
  'sectionExclude',
] as const;
export type RssArticleMatchingFilter = Partial<
  Record<typeof RSS_MATCH_FIELDS[number], string[]>
>;
export const KIT_MATCH_FIELDS = [
  'titleInclude',
  'titleExclude',
  'urlInclude',
  'urlExclude',
] as const;
export type KitPostMatchingFilter = Partial<
  Record<(typeof KIT_MATCH_FIELDS)[number], string[]>
>;
export type QuizQuestionSource =
  | { kind: 'rss_filter'; filter: RssArticleMatchingFilter }
  | { kind: 'kit_filter'; filter: KitPostMatchingFilter }
  | { kind: 'kit_post'; postId: string }
  | { kind: 'article'; url: string };
export type QuizQuestionSlot = {
  id: string;
  itemType: QuizQuestionSlotItemType;
  pollOptionCount?: number;
  source?: QuizQuestionSource;
};
export type QuizQuestionSlotOutcome = {
  slotId: string;
  status: 'ready' | 'missing_source';
  sourceUrl?: string;
  sourceTitle?: string;
  sourceLocator?: string;
  reason?: string;
};
export const MAX_QUIZ_QUESTION_SLOTS = 500;

function isRecord(value: unknown): value is Record<string, unknown> {
  return Boolean(value) && typeof value === 'object' && !Array.isArray(value);
}

export function parseQuizQuestionSource(
  value: unknown,
): QuizQuestionSource | null {
  if (!isRecord(value)) return null;
  if (value.kind === 'article') {
    if (typeof value.url !== 'string' || !value.url.trim()) return null;
    try {
      const url = new URL(value.url.trim());
      if (
        !['http:', 'https:'].includes(url.protocol) || url.username ||
        url.password
      ) return null;
      return { kind: 'article', url: url.toString() };
    } catch {
      return null;
    }
  }
  if (value.kind === 'kit_post') {
    const postId = typeof value.postId === 'string'
      ? value.postId.trim()
      : typeof value.post_id === 'string'
      ? value.post_id.trim()
      : '';
    if (!postId || postId.length > 64 || !/^\d+$/.test(postId)) return null;
    return { kind: 'kit_post', postId };
  }
  if (
    (value.kind !== 'rss_filter' && value.kind !== 'kit_filter') ||
    !isRecord(value.filter)
  ) {
    return null;
  }
  const allowedFields = value.kind === 'kit_filter'
    ? KIT_MATCH_FIELDS
    : RSS_MATCH_FIELDS;
  if (
    Object.keys(value.filter).some((key) =>
      !(allowedFields as readonly string[]).includes(key)
    )
  ) return null;
  const filter: RssArticleMatchingFilter = {};
  for (const key of allowedFields) {
    const terms = value.filter[key];
    if (terms === undefined) continue;
    if (
      !Array.isArray(terms) || terms.length > 50 ||
      terms.some((term) => typeof term !== 'string' || term.length > 500)
    ) return null;
    const normalized = [
      ...new Set(
        (terms as string[]).map((term) => term.trim()).filter(Boolean),
      ),
    ];
    if (normalized.length) filter[key] = normalized;
  }
  return value.kind === 'kit_filter'
    ? { kind: 'kit_filter', filter }
    : { kind: 'rss_filter', filter };
}

export function parseQuizQuestionSlots(
  value: unknown,
): QuizQuestionSlot[] | null {
  if (
    !Array.isArray(value) || value.length === 0 ||
    value.length > MAX_QUIZ_QUESTION_SLOTS
  ) return null;
  const ids = new Set<string>();
  const slots: QuizQuestionSlot[] = [];
  for (const entry of value) {
    if (!isRecord(entry)) return null;
    const id = typeof entry.id === 'string' ? entry.id.trim() : '';
    if (!id || id.length > 200 || ids.has(id)) return null;
    if (entry.itemType !== 'trivia' && entry.itemType !== 'poll') return null;
    const source = entry.source === undefined
      ? undefined
      : parseQuizQuestionSource(entry.source);
    if (source === null) return null;
    const optionCount = typeof entry.pollOptionCount === 'number' &&
        Number.isFinite(entry.pollOptionCount)
      ? Math.max(2, Math.min(6, Math.trunc(entry.pollOptionCount)))
      : 4;
    ids.add(id);
    slots.push({
      id,
      itemType: entry.itemType,
      ...(entry.itemType === 'poll' ? { pollOptionCount: optionCount } : {}),
      ...(source ? { source } : {}),
    });
  }
  return slots;
}

export function parseQuizQuestionSlotOutcomes(
  value: unknown,
): QuizQuestionSlotOutcome[] | null {
  if (!Array.isArray(value) || value.length > MAX_QUIZ_QUESTION_SLOTS) {
    return null;
  }
  const ids = new Set<string>();
  const outcomes: QuizQuestionSlotOutcome[] = [];
  for (const entry of value) {
    if (
      !isRecord(entry) || typeof entry.slotId !== 'string' ||
      !entry.slotId.trim() || ids.has(entry.slotId)
    ) return null;
    if (entry.status !== 'ready' && entry.status !== 'missing_source') {
      return null;
    }
    ids.add(entry.slotId);
    const outcome: QuizQuestionSlotOutcome = {
      slotId: entry.slotId,
      status: entry.status,
    };
    for (
      const key of [
        'sourceUrl',
        'sourceTitle',
        'sourceLocator',
        'reason',
      ] as const
    ) {
      if (entry[key] !== undefined && typeof entry[key] !== 'string') {
        return null;
      }
      if (typeof entry[key] === 'string' && entry[key].trim()) {
        outcome[key] = entry[key].trim();
      }
    }
    outcomes.push(outcome);
  }
  return outcomes;
}
